import { vec2 } from 'gl-matrix';
import { CurveData } from '~/components/Charts/DVHLineChart';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import {
    DEFAULT_MAX_DOSE_RANGE,
    DEFAULT_MIN_DOSE_RANGE,
} from '~/engine/data/dose/DoseDistribution';
import { DoseData } from '~/state/atoms/dose/doseAtoms';
import { DosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';

export default function transformDoseMatrixToDoseData(
    doseMatrix: DoseMatrix,
    doseScale: number,
    dose?: DoseData,
    dosePredictionTask?: DosePredictionTaskState
): DoseData {
    const dvhCurveData: CurveData[] = [];

    for (const [roi, dvh] of doseMatrix.getDvhs() ?? []) {
        dvhCurveData.push({
            data: dvh.dvhData.map((y, x) => ({
                x: x * dvh.binWidth * doseScale,
                y,
            })),
            color: `rgb(${roi.color.R}, ${roi.color.G}, ${roi.color.B})`,
            name: roi.name,
            visible: roi.visible,
            maxDose: dvh.maxDose * doseScale,
            minDose: dvh.minDose * doseScale,
            meanDose: dvh.meanDose * doseScale,
            roiNumber: roi.roiNumber,
        });
    }

    const selectedRange =
        dose?.selectedRange ??
        vec2.fromValues(DEFAULT_MIN_DOSE_RANGE, DEFAULT_MAX_DOSE_RANGE);
    const manufacturer = doseMatrix.dicomDose.manufacturer;
    const generated = manufacturer === 'MVision AI';
    const targets =
        dose?.targets ?? (generated ? dosePredictionTask?.targets : undefined);
    const manufacturerModelName = doseMatrix.dicomDose.manufacturerModelName;
    const seriesDescription = doseMatrix.dicomDose.seriesDescription;
    const doseComment = doseMatrix.dicomDose.doseComment;
    const softwareVersions = doseMatrix.dicomDose.softwareVersions;
    const doseGridResolution = doseMatrix.grid.getRes();

    return {
        id: doseMatrix.dicomUid,
        sopUid: doseMatrix.dicomUid,
        normalizationValueGy: doseMatrix.doseMaxGy * doseScale,
        dvhs: dvhCurveData,
        type: doseMatrix.doseType,
        summationType: doseMatrix.doseSummationType,
        doseGridResolution,
        maxDoseGy: doseMatrix.doseMaxGy * doseScale,
        generated,
        manufacturer,
        manufacturerModelName,
        seriesDescription,
        doseComment,
        softwareVersions,
        selectedRange,
        targets,
    };
}
